import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useRateCardsActions } from "tap/store/rateCards/useRateCards.js";
import { tapApi } from "tap/api.js";
import { getTierName } from "../utils/payloadBuilder";
import {
  getTapWindowIdForPriceColumn,
  normalizePriceInput,
  sortRateCardWindows,
} from "../utils/validation";

/**
 * Custom hook for managing rate card form operations in EDIT mode.
 * @returns {Object} - Form state and handlers for edit mode
 */
export function useEditRateCardForm() {
  const {
    currencyOptions,
    formatOptions,
    windowSequenceOptions,
    priceTypeOptions,
    revShareOptions,
    selectedRateCard,
  } = useSelector((state) => state.rateCards);

  const { updateRateCardForm } = useRateCardsActions();

  const formData = selectedRateCard;
  const stateKey = "selectedRateCard";

  const tierFormats = useMemo(
    () => formData?.tierAliases || [],
    [formData?.tierAliases],
  );

  const handleInputChange = useCallback(
    async (field, value) => {
      if (field.startsWith("percentage_")) {
        const tapWindowId = field.split("_")[1];
        const updatedWindows = (formData.windows || []).map((win) =>
          String(win.id.tapWindowId) === String(tapWindowId)
            ? { ...win, percentage: value }
            : win,
        );
        updateRateCardForm({
          key: stateKey,
          data: { windows: updatedWindows },
        });
        return;
      }

      if (field === "windowSequence") {
        const windowSequenceId = value?.data?.id;
        const clearedTierAliases = (formData?.tierAliases || []).map(
          (alias) => ({ ...alias, tiers: [] }),
        );

        const data = await tapApi.windowSequenceById(windowSequenceId);
        const windows = Array.isArray(data.windows)
          ? data.windows.map((w) => ({
              id: { rateCardId: formData.id, tapWindowId: w.id },
              window: { ...w },
              percentage: "",
            }))
          : [];

        updateRateCardForm({
          key: stateKey,
          data: {
            tierAliases: clearedTierAliases,
            windowSequenceId,
            windowSequence: value?.data,
            windows,
          },
        });
        return;
      }

      if (field === "priceType") {
        const clearedTierAliases = (formData?.tierAliases || []).map(
          (alias) => ({ ...alias, tiers: [] }),
        );
        updateRateCardForm({
          key: stateKey,
          data: {
            tierAliases: clearedTierAliases,
            [field]: value?.label || value,
          },
        });
        return;
      }

      if (field === "currency" || field === "revShareType") {
        updateRateCardForm({
          key: stateKey,
          data: { [field]: value?.label || value },
        });
        return;
      }

      updateRateCardForm({
        key: stateKey,
        data: { [field]: value },
      });
    },
    [formData, updateRateCardForm],
  );

  const handleAddTierFormat = useCallback(() => {
    if (
      !Array.isArray(formData?.tierAliases) ||
      formData.tierAliases.length === 0
    ) {
      updateRateCardForm({
        key: stateKey,
        data: {
          tierAliases: [
            {
              index: 0,
              rateCardId: formData.id,
              sortOrder: 1,
              tierAliasName: "",
              tierAliasFormat: [],
              tiers: [],
            },
          ],
        },
      });
      return;
    }

    const tierAliases = formData.tierAliases;
    const lastAlias = tierAliases[tierAliases.length - 1];
    const sortOrder = lastAlias.sortOrder
      ? parseInt(lastAlias.sortOrder, 10) + 1
      : 1;

    const tiersToAdd = Array.isArray(lastAlias.tiers)
      ? lastAlias.tiers.map((tier) => {
          const prevTierName = parseInt(tier.tierName, 10);
          const newTierName =
            prevTierName >= 100
              ? (prevTierName - tier.id.tierNumber) * 10 + tier.id.tierNumber
              : prevTierName + 100;
          return {
            id: {
              tapWindowId: tier.id.tapWindowId,
              tierNumber: tier.id.tierNumber,
            },
            tierName: newTierName,
            price: tier.price,
            sortOrder: tier.sortOrder,
          };
        })
      : [];

    const newAlias = {
      index: tierAliases.length,
      rateCardId: formData.id,
      tierAliasName: "",
      tierAliasFormat: [],
      tiers: tiersToAdd,
      sortOrder,
    };

    updateRateCardForm({
      key: stateKey,
      data: { tierAliases: [...tierAliases, newAlias] },
    });
  }, [formData, updateRateCardForm]);

  const handleRemoveTierFormat = useCallback(
    (idx) => {
      const tierAliases = formData?.tierAliases || [];
      if (idx < 0 || idx >= tierAliases.length) return;

      const updatedTierAliases = tierAliases
        .filter((_, i) => i !== idx)
        .map((alias, newIdx) => ({
          ...alias,
          index: newIdx,
        }));

      updateRateCardForm({
        key: stateKey,
        data: { tierAliases: updatedTierAliases },
      });
    },
    [formData, updateRateCardForm],
  );

  const handleTierFormatChange = useCallback(
    (idx, field, value) => {
      const updatedAliases = tierFormats.map((t, i) =>
        i === idx ? { ...t, [field]: value } : t,
      );
      updateRateCardForm({
        key: stateKey,
        data: { tierAliases: updatedAliases },
      });
    },
    [tierFormats, updateRateCardForm],
  );

  const handleAddTierRow = useCallback(() => {
    const tierAliases = formData?.tierAliases || [];
    if (tierAliases.length === 0) return;

    const hasTiers = tierAliases.some(
      (alias) => Array.isArray(alias.tiers) && alias.tiers.length > 0,
    );

    if (!hasTiers) {
      const windowIds = sortRateCardWindows(formData?.windows || []).map(
        (w) => w.id.tapWindowId || w.id,
      );
      const updatedTierAliases = tierAliases.map((alias, aliasIdx) => {
        const newTiers = windowIds.map((tapWindowId, winIdx) => ({
          id: {
            tierAliasId: alias.id,
            tapWindowId,
            tierNumber: 1,
          },
          tierName: getTierName(0, aliasIdx).toString(),
          price: "",
          sortOrder: winIdx,
        }));
        return { ...alias, tiers: newTiers };
      });
      updateRateCardForm({
        key: stateKey,
        data: { tierAliases: updatedTierAliases },
      });
      return;
    }

    const firstAlias = tierAliases[0];
    const windowIds = sortRateCardWindows(formData?.windows || []).map(
      (w) => w.id.tapWindowId || w.id,
    );
    const uniqueWindowIds = Array.from(new Set(windowIds));
    const tierNumbers = firstAlias?.tiers?.map((t) => t.id.tierNumber) || [];
    const maxTierNumber = Math.max(0, ...tierNumbers);
    const newTierNumber = maxTierNumber + 1;

    const updatedTierAliases = tierAliases.map((alias, aliasIdx) => {
      const lastTierName =
        alias.tiers?.length > 0
          ? parseInt(alias.tiers[alias.tiers.length - 1].tierName, 10)
          : aliasIdx === 0
            ? 0
            : aliasIdx === 1
              ? 100
              : Math.pow(10, aliasIdx + 1);
      const newTierName = (lastTierName + 1).toString();

      const newTiers = uniqueWindowIds.map((tapWindowId, winIdx) => ({
        id: {
          tierAliasId: alias.id,
          tapWindowId,
          tierNumber: newTierNumber,
        },
        tierName: newTierName,
        price: "",
        sortOrder: alias.tiers.length + winIdx,
      }));
      return { ...alias, tiers: [...alias.tiers, ...newTiers] };
    });

    updateRateCardForm({
      key: stateKey,
      data: { tierAliases: updatedTierAliases },
    });
  }, [formData, updateRateCardForm]);

  const handleRemoveTierRow = useCallback(
    (row) => {
      const tierAliases = formData?.tierAliases || [];
      const tierNumberToRemove = row[0]?.id?.tierNumber;
      if (tierNumberToRemove === undefined) return;

      const updatedTierAliases = tierAliases.map((alias) => ({
        ...alias,
        tiers: (alias.tiers || []).filter(
          (tier) => tier.id.tierNumber !== tierNumberToRemove,
        ),
      }));

      updateRateCardForm({
        key: stateKey,
        data: { tierAliases: updatedTierAliases },
      });
    },
    [formData, updateRateCardForm],
  );

  const handleTierRowInputChange = useCallback(
    (tierNumberParam, colType, colIdx, value) => {
      const tierAliases = formData?.tierAliases || [];
      if (tierAliases.length === 0) return;

      const tierNumber = Number(tierNumberParam);
      const targetTapWindowId = getTapWindowIdForPriceColumn(
        formData?.windows,
        colIdx,
      );
      const nextValue =
        colType === "price" ? normalizePriceInput(value) : value;

      let updatedTierAliases;
      if (colType === "tierName") {
        updatedTierAliases = tierAliases.map((alias, aliasIdx) => {
          if (aliasIdx === colIdx) {
            const updatedTiers = alias.tiers.map((tier) => {
              if (tier.id.tierNumber === tierNumber) {
                return { ...tier, tierName: nextValue };
              }
              return tier;
            });
            return { ...alias, tiers: updatedTiers };
          }
          return alias;
        });
      } else if (colType === "price") {
        updatedTierAliases = tierAliases.map((alias) => {
          const updatedTiers = alias.tiers.map((tier) => {
            if (
              tier.id.tierNumber === tierNumber &&
              String(tier.id.tapWindowId) === String(targetTapWindowId)
            ) {
              return { ...tier, price: nextValue };
            }
            return tier;
          });
          return { ...alias, tiers: updatedTiers };
        });
      } else {
        updatedTierAliases = tierAliases;
      }

      updateRateCardForm({
        key: stateKey,
        data: { tierAliases: updatedTierAliases },
      });
    },
    [formData, updateRateCardForm],
  );

  const showTierFormat = useMemo(() => tierFormats.length > 0, [tierFormats]);
  const priceType = useMemo(
    () => formData?.priceType || "",
    [formData?.priceType],
  );

  return {
    // Form data and options
    formData,
    tierFormats,
    currencyOptions,
    formatOptions,
    windowSequenceOptions,
    priceTypeOptions,
    revShareOptions,

    // Handlers
    handleInputChange,
    handleAddTierFormat,
    handleRemoveTierFormat,
    handleTierFormatChange,
    handleAddTierRow,
    handleRemoveTierRow,
    handleTierRowInputChange,

    // Computed values
    showTierFormat,
    priceType,
  };
}
